import React from 'react';
import { Reveal } from '../components/Reveal';
import { Link } from 'react-router-dom';
import { Leaf, Play, AlertTriangle, Recycle, ArrowRight, Heart, Trash2, Globe } from 'lucide-react';
import { Button } from '../components/ui/button';
import { useSection, resolveImage } from '../contexts/ContentContext';
import PauseOnScroll from '../components/PauseOnScroll';

export default function Impact() {
  const data = useSection('impact');
  const hero = data.hero || {};
  const intro = data.intro || {};
  const videos = data.videos || [];
  const finalCta = data.finalCta || {};

  return (
    <>
      {/* HERO */}
      <section className="pt-32 pb-16 bg-gradient-to-br from-rose-50 via-white to-emerald-50 relative overflow-hidden">
        <div className="absolute top-32 -left-32 w-96 h-96 bg-rose-200/30 rounded-full blur-3xl"></div>
        <div className="absolute bottom-0 -right-32 w-96 h-96 bg-emerald-200/30 rounded-full blur-3xl"></div>

        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Reveal>
            {hero.eyebrow && (
              <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-white border border-rose-100 shadow-sm text-xs font-bold uppercase tracking-wider text-rose-700 mb-5">
                <AlertTriangle className="w-3.5 h-3.5" />{hero.eyebrow}
              </span>
            )}
            <h1 className="font-display font-extrabold text-5xl lg:text-7xl text-sky-950 mb-6 leading-tight">
              {hero.title} <span className="gradient-text">{hero.titleHighlight}</span>
            </h1>
            <p className="text-lg lg:text-xl text-slate-600 leading-relaxed max-w-2xl mx-auto">{hero.description}</p>
          </Reveal>
        </div>
      </section>

      {/* INTRO PARAGRAPHS */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-3 gap-5 mb-14">
            <Reveal>
              <div className="p-6 rounded-3xl bg-gradient-to-br from-rose-50 to-orange-50 border border-rose-100 text-center h-full">
                <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-rose-400 to-orange-500 mx-auto mb-4 flex items-center justify-center">
                  <Trash2 className="w-7 h-7 text-white" />
                </div>
                <p className="font-display font-bold text-2xl text-rose-700 mb-1">Millions</p>
                <p className="text-sm text-slate-600">de bouteilles jetées chaque jour</p>
              </div>
            </Reveal>
            <Reveal delay={100}>
              <div className="p-6 rounded-3xl bg-gradient-to-br from-amber-50 to-yellow-50 border border-amber-100 text-center h-full">
                <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-amber-400 to-orange-500 mx-auto mb-4 flex items-center justify-center">
                  <Globe className="w-7 h-7 text-white" />
                </div>
                <p className="font-display font-bold text-2xl text-amber-700 mb-1">Transport</p>
                <p className="text-sm text-slate-600">achetées, transportées, jetées</p>
              </div>
            </Reveal>
            <Reveal delay={200}>
              <div className="p-6 rounded-3xl bg-gradient-to-br from-emerald-50 to-sky-50 border border-emerald-100 text-center h-full">
                <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-emerald-400 to-teal-500 mx-auto mb-4 flex items-center justify-center">
                  <Recycle className="w-7 h-7 text-white" />
                </div>
                <p className="font-display font-bold text-2xl text-emerald-700 mb-1">Alternative</p>
                <p className="text-sm text-slate-600">bouteille réutilisable + eau locale</p>
              </div>
            </Reveal>
          </div>

          <div className="space-y-6 max-w-3xl mx-auto">
            {(intro.paragraphs || []).map((p, i) => (
              <Reveal key={i} delay={i * 80}>
                <p className="text-lg text-slate-700 leading-relaxed relative pl-6 border-l-4 border-sky-200">{p}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* DANGERS - Microplastiques */}
      {data.dangers?.title && (
        <section className="py-20 bg-gradient-to-br from-rose-50 to-orange-50">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <Reveal>
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white text-rose-700 text-xs font-bold uppercase tracking-wider mb-5 shadow-sm">
                <AlertTriangle className="w-3.5 h-3.5" />Alerte santé
              </div>
              <h2 className="font-display font-extrabold text-3xl lg:text-4xl text-sky-950 mb-6">{data.dangers.title}</h2>
              <div className="space-y-4">
                {(data.dangers.paragraphs || []).map((p, i) => (
                  <p key={i} className="text-slate-700 leading-relaxed" dangerouslySetInnerHTML={{ __html: p }} />
                ))}
              </div>
            </Reveal>
          </div>
        </section>
      )}

      {/* PETROL */}
      {data.petrol?.title && (
        <section className="py-20 bg-white">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
            <Reveal>
              <div className="text-center mb-10">
                <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-amber-50 text-amber-700 text-xs font-bold uppercase tracking-wider mb-4">
                  <Globe className="w-3.5 h-3.5" />Fabrication
                </div>
                <h2 className="font-display font-extrabold text-3xl lg:text-4xl text-sky-950 mb-3">{data.petrol.title}</h2>
                <p className="text-slate-600 max-w-2xl mx-auto">{data.petrol.intro}</p>
              </div>
              <div className="grid md:grid-cols-2 gap-5 mb-8">
                <div className="p-6 rounded-3xl bg-gradient-to-br from-sky-50 to-emerald-50 border border-sky-100">
                  <h3 className="font-display font-bold text-sky-950 mb-4">Chaque année</h3>
                  <ul className="space-y-2">
                    {(data.petrol.yearly || []).map((y, i) => (
                      <li key={i} className="flex items-start gap-2 text-sm text-slate-700"><Trash2 className="w-4 h-4 text-rose-500 flex-shrink-0 mt-0.5" /><span>{y}</span></li>
                    ))}
                  </ul>
                </div>
                <div className="p-6 rounded-3xl bg-gradient-to-br from-amber-50 to-orange-50 border border-amber-100">
                  <h3 className="font-display font-bold text-sky-950 mb-4">Fabrication PET</h3>
                  <ul className="space-y-2">
                    {(data.petrol.manufacture || []).map((y, i) => (
                      <li key={i} className="flex items-start gap-2 text-sm text-slate-700"><Recycle className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" /><span>{y}</span></li>
                    ))}
                  </ul>
                </div>
              </div>
              <p className="text-center font-display font-bold text-lg text-rose-700 max-w-3xl mx-auto">{data.petrol.conclusion}</p>
            </Reveal>
          </div>
        </section>
      )}

      {/* TRANSPORT */}
      {data.transport?.title && (
        <section className="py-20 bg-gradient-to-br from-sky-50 to-emerald-50">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
            <Reveal>
              <h2 className="font-display font-extrabold text-3xl lg:text-4xl text-sky-950 mb-6 text-center">{data.transport.title}</h2>
              <div className="space-y-4 max-w-3xl mx-auto mb-8">
                {(data.transport.paragraphs || []).map((p, i) => (
                  <p key={i} className="text-slate-700 leading-relaxed">{p}</p>
                ))}
              </div>
              <div className="p-6 rounded-3xl bg-gradient-to-br from-emerald-500 to-sky-500 text-white shadow-xl max-w-3xl mx-auto">
                <div className="flex items-start gap-3">
                  <Leaf className="w-6 h-6 flex-shrink-0 mt-0.5" />
                  <p className="leading-relaxed">{data.transport.kiosque}</p>
                </div>
              </div>
            </Reveal>
          </div>
        </section>
      )}

      {/* WASTE */}
      {data.waste?.title && (
        <section className="py-20 bg-white">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
            <Reveal>
              <h2 className="font-display font-extrabold text-3xl lg:text-4xl text-sky-950 mb-6 text-center">{data.waste.title}</h2>
              <div className="grid lg:grid-cols-[1fr_auto] gap-8 items-center">
                <div className="space-y-4">
                  {(data.waste.paragraphs || []).map((p, i) => (
                    <p key={i} className="text-slate-700 leading-relaxed">{p}</p>
                  ))}
                </div>
                {data.waste.highlight && (
                  <div className="p-6 rounded-3xl bg-gradient-to-br from-sky-950 to-emerald-950 text-white text-center max-w-xs">
                    <p className="font-display font-extrabold text-3xl leading-tight">{data.waste.highlight.line1}</p>
                    <p className="font-display font-extrabold text-xl mt-2 leading-tight">{data.waste.highlight.line2}</p>
                    <div className="w-16 h-0.5 bg-yellow-400 mx-auto my-3"></div>
                    <p className="text-sm text-sky-200 uppercase font-semibold">{data.waste.highlight.line3}</p>
                  </div>
                )}
              </div>
            </Reveal>
          </div>
        </section>
      )}

      {/* COST */}
      {data.cost?.title && (
        <section className="py-20 bg-gradient-to-br from-amber-50 to-orange-50">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
            <Reveal>
              <div className="text-center mb-10">
                <h2 className="font-display font-extrabold text-3xl lg:text-4xl text-sky-950 mb-4">{data.cost.title}</h2>
                <p className="text-slate-600 max-w-2xl mx-auto">{data.cost.intro}</p>
              </div>
              <div className="grid md:grid-cols-2 gap-6 mb-10">
                <div className="p-6 rounded-3xl bg-white border border-sky-100">
                  <h3 className="font-display font-bold text-sky-950 mb-4">Prix moyen au litre</h3>
                  <div className="space-y-2">
                    {(data.cost.prices || []).map((p, i) => (
                      <div key={i} className="flex items-center justify-between p-3 rounded-xl bg-sky-50/50">
                        <span className="text-sm text-slate-700">{p.type}</span>
                        <span className="font-display font-bold text-sky-900">{p.price}</span>
                      </div>
                    ))}
                  </div>
                </div>
                <div className="p-6 rounded-3xl bg-white border border-sky-100">
                  <h3 className="font-display font-bold text-sky-950 mb-2">Pour une famille (500 L/an)</h3>
                  <p className="text-xs text-slate-500 mb-4">{data.cost.familyIntro}</p>
                  <div className="space-y-2">
                    {(data.cost.family || []).map((p, i) => (
                      <div key={i} className="flex items-center justify-between p-3 rounded-xl bg-emerald-50/50">
                        <span className="text-sm text-slate-700">{p.label}</span>
                        <span className="font-display font-bold text-emerald-700">{p.cost}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
              <p className="text-center text-slate-700 max-w-3xl mx-auto italic leading-relaxed">{data.cost.conclusion}</p>
            </Reveal>
          </div>
        </section>
      )}

      {/* VIDEO SECTION */}
      <section className="py-20 bg-gradient-to-br from-sky-950 to-emerald-950 text-white relative overflow-hidden">
        <div className="absolute -top-40 -left-40 w-96 h-96 bg-rose-500/10 rounded-full blur-3xl"></div>
        <div className="absolute -bottom-40 -right-40 w-96 h-96 bg-emerald-500/10 rounded-full blur-3xl"></div>

        <div className="relative max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          {data.videoIntro && (
            <Reveal>
              <p className="text-lg text-sky-100 leading-relaxed text-center max-w-3xl mx-auto mb-10">{data.videoIntro}</p>
            </Reveal>
          )}

          <div className="space-y-16">
            {videos.map((v, i) => (
              <Reveal key={i}>
                <div data-testid={`impact-video-block-${i}`}>
                  {v.introBlock && (v.introBlock.title || (v.introBlock.paragraphs || []).length > 0) && (
                    <div className="max-w-3xl mx-auto mb-10 text-sky-100">
                      {v.introBlock.title && (
                        <h3 className="font-display font-extrabold text-2xl lg:text-3xl text-white text-center mb-6" data-testid={`impact-video-intro-title-${i}`}>
                          {v.introBlock.title}
                        </h3>
                      )}
                      <div className="space-y-4">
                        {(v.introBlock.paragraphs || []).map((p, pi) => (
                          <p key={pi} className="leading-relaxed" dangerouslySetInnerHTML={{ __html: p }} />
                        ))}
                      </div>
                      {v.introBlock.listTitle && (
                        <p className="mt-6 font-display font-bold text-white text-lg">{v.introBlock.listTitle}</p>
                      )}
                      {v.introBlock.listIntro && (
                        <p className="mt-3 leading-relaxed">{v.introBlock.listIntro}</p>
                      )}
                      {Array.isArray(v.introBlock.bullets) && v.introBlock.bullets.length > 0 && (
                        <ul className="mt-3 space-y-2">
                          {v.introBlock.bullets.map((b, bi) => (
                            <li key={bi} className="flex items-start gap-2.5">
                              <span className="w-1.5 h-1.5 rounded-full bg-emerald-300 mt-2 flex-shrink-0"></span>
                              <span dangerouslySetInnerHTML={{ __html: b }} />
                            </li>
                          ))}
                        </ul>
                      )}
                      {v.introBlock.videoLead && (
                        <p className="mt-6 leading-relaxed">{v.introBlock.videoLead}</p>
                      )}
                    </div>
                  )}

                  <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-yellow-400 text-slate-900 text-xs font-bold uppercase tracking-wider mb-4 shadow-lg">
                    <Play className="w-3.5 h-3.5 fill-slate-900" />VIDÉO {v.number} · {v.title}
                  </div>

                  <div className="relative aspect-video rounded-3xl overflow-hidden bg-black/40 border border-white/10 shadow-2xl group">
                    <PauseOnScroll className="absolute inset-0 w-full h-full">
                    {v.url ? (
                      v.url.includes('youtube.com') || v.url.includes('youtu.be') ? (
                        <iframe
                          src={v.url.replace('watch?v=', 'embed/').replace('youtu.be/', 'youtube.com/embed/')}
                          title={v.title}
                          className="w-full h-full"
                          allowFullScreen
                        />
                      ) : (
                        <video src={v.url} controls className="w-full h-full object-cover" />
                      )
                    ) : (
                      <div className="w-full h-full flex items-center justify-center relative">
                        {v.thumbnail && <img src={resolveImage(v.thumbnail)} alt="" className="absolute inset-0 w-full h-full object-cover opacity-60" />}
                        <div className="relative text-center">
                          <div className="w-24 h-24 rounded-full bg-white/10 backdrop-blur flex items-center justify-center mx-auto mb-4 group-hover:bg-white/20 transition-colors cursor-pointer ring-4 ring-white/20">
                            <Play className="w-10 h-10 text-white ml-1 fill-white" />
                          </div>
                          <p className="font-display font-bold text-xl">{v.title}</p>
                          {v.source && <p className="text-sm text-sky-300 mt-1">{v.source}</p>}
                          <p className="text-xs text-sky-400 mt-3 italic">Vidéo à téléverser depuis le CMS</p>
                        </div>
                      </div>
                    )}
                    </PauseOnScroll>
                  </div>
                  {v.source && (
                    <p className="text-xs text-sky-300 mt-3 text-center italic">Source : {v.source}</p>
                  )}
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="py-20 bg-gradient-to-br from-emerald-50 to-sky-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Reveal>
            <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-emerald-400 to-sky-500 flex items-center justify-center mx-auto mb-6 shadow-lg">
              <Leaf className="w-8 h-8 text-white" />
            </div>
            <h2 className="font-display font-extrabold text-4xl lg:text-5xl text-sky-950 mb-5">{finalCta.title}</h2>
            <p className="text-lg text-slate-700 mb-8 max-w-2xl mx-auto">{finalCta.description}</p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/contact">
                <Button size="lg" className="bg-gradient-to-r from-sky-500 to-emerald-500 text-white rounded-xl px-7 font-semibold" data-testid="impact-cta-contact">
                  Nous contacter<ArrowRight className="ml-2 w-4 h-4" />
                </Button>
              </Link>
              <Link to="/je-soutiens">
                <Button size="lg" variant="outline" className="border-2 border-sky-300 text-sky-900 hover:bg-white rounded-xl px-7" data-testid="impact-cta-support">
                  <Heart className="mr-2 w-4 h-4" />Soutenir le projet
                </Button>
              </Link>
            </div>
          </Reveal>
        </div>
      </section>
    </>
  );
}
